import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Pencil, Paperclip, Send, Megaphone, Image as ImageIcon, FileText, MessageCircle, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "corte", label: "✂️ Corte" },
  { value: "luzes", label: "✨ Luzes / Mechas" },
  { value: "hair_nutri", label: "🧴 Hair Nutri" },
  { value: "aniversario", label: "🎂 Aniversário" },
  { value: "vip", label: "❤️ Cliente VIP" },
  { value: "pos_venda", label: "📣 Pós-venda" },
  { value: "financeiro", label: "💰 Financeiro" },
  { value: "outro", label: "🗂️ Outro" },
];

const FUNNEL_OPTS = [
  { value: "", label: "— Não alterar status" },
  { value: "amarelo", label: "🟡 Campanha educativa" },
  { value: "laranja", label: "🟠 Convite p/ agenda" },
  { value: "verde", label: "🟢 Agendou" },
  { value: "azul", label: "🔵 Atendimento realizado" },
  { value: "vermelho", label: "🔴 Sem resposta" },
];

const EMPTY = { name: "", category: "corte", text: "", default_funnel_status: "", active: true };

export default function Campaigns() {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filterCat, setFilterCat] = useState("all");
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTemplate, setSendTemplate] = useState(null);
  const [waStatus, setWaStatus] = useState(null);

  const checkWAStatus = async () => {
    try {
      const r = await apiCall("get", "/whatsapp/status");
      setWaStatus(r.data);
    } catch {
      setWaStatus({ configured: false, connected: false });
    }
  };

  useEffect(() => { checkWAStatus(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiCall("get", "/campaign-templates");
      setTemplates(r.data || []);
    } catch (err) {
      toast.error("Erro ao carregar templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredTemplates = useMemo(() => {
    if (filterCat === "all") return templates;
    return templates.filter((t) => t.category === filterCat);
  }, [templates, filterCat]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Preencha nome e texto");
      return;
    }
    try {
      const payload = {
        ...form,
        default_funnel_status: form.default_funnel_status || null,
      };
      if (editing) {
        await apiCall("put", `/campaign-templates/${editing.template_id}`, payload);
        toast.success("Template atualizado");
      } else {
        await apiCall("post", "/campaign-templates", payload);
        toast.success("Template criado");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      category: t.category,
      text: t.text,
      default_funnel_status: t.default_funnel_status || "",
      active: t.active,
    });
    setOpen(true);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Excluir template "${t.name}"?`)) return;
    try {
      await apiCall("delete", `/campaign-templates/${t.template_id}`);
      toast.success("Template excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const handleUploadAttachment = async (t, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const tk = localStorage.getItem("access_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/campaign-templates/${t.template_id}/attachments`;
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${tk}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Erro upload");
      toast.success(`Anexo "${data.filename}" adicionado`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteAttachment = async (t, attId) => {
    try {
      await apiCall("delete", `/campaign-templates/${t.template_id}/attachments/${attId}`);
      toast.success("Anexo removido");
      load();
    } catch {
      toast.error("Erro ao remover anexo");
    }
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <PageHeader
        overline="CRM"
        title="Biblioteca de Campanhas"
        subtitle="Templates reutilizáveis com texto + imagem/PDF"
      >
        <Button
          variant="outline"
          onClick={() => navigate("/campanhas/jobs")}
          data-testid="goto-jobs-btn"
        >
          <MessageCircle className="h-4 w-4 mr-2 text-emerald-700" strokeWidth={1.5} />
          Histórico de disparos
        </Button>
        <Button
          onClick={() => {
            setEditing(null);
            setForm(EMPTY);
            setOpen(true);
          }}
          data-testid="new-template-button"
        >
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Novo Template
        </Button>
      </PageHeader>

      {/* WhatsApp status banner */}
      {waStatus && (
        <Card
          className={`p-3 border ${
            waStatus.configured && waStatus.connected
              ? "border-emerald-300 bg-emerald-50/50"
              : "border-amber-300 bg-amber-50/50"
          }`}
          data-testid="wa-status-banner"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
            <div className="flex items-center gap-2">
              {waStatus.configured && waStatus.connected ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" strokeWidth={1.5} />
                  <span className="font-medium text-emerald-900">WhatsApp conectado (Z-API)</span>
                  <span className="text-muted-foreground">· pronto pra disparar campanhas</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-700" strokeWidth={1.5} />
                  <span className="font-medium text-amber-900">
                    WhatsApp {waStatus.configured ? "desconectado" : "não configurado"}
                  </span>
                  {waStatus.error && (
                    <span className="text-muted-foreground">· {waStatus.error}</span>
                  )}
                </>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={checkWAStatus} className="h-7 text-[11px]" data-testid="wa-status-refresh">
              Verificar
            </Button>
          </div>
        </Card>
      )}

      {/* Category filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="category-filters">
        <button
          type="button"
          onClick={() => setFilterCat("all")}
          className={`text-xs px-3 py-1.5 rounded-md border ${filterCat === "all" ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}
          data-testid="cat-all"
        >
          Todas ({templates.length})
        </button>
        {CATEGORIES.map((c) => {
          const n = templates.filter((t) => t.category === c.value).length;
          if (n === 0 && filterCat !== c.value) return null;
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => setFilterCat(c.value)}
              className={`text-xs px-3 py-1.5 rounded-md border ${filterCat === c.value ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}
              data-testid={`cat-${c.value}`}
            >
              {c.label} ({n})
            </button>
          );
        })}
      </div>

      {/* Templates list */}
      {loading ? (
        <Card className="p-8 text-center text-muted-foreground">Carregando...</Card>
      ) : filteredTemplates.length === 0 ? (
        <Card className="p-12 text-center border border-border bg-card">
          <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-30" strokeWidth={1.2} />
          <p className="text-sm text-muted-foreground mb-1">Nenhum template ainda.</p>
          <p className="text-xs text-muted-foreground">
            Crie templates como "Corte +90 dias", "Aniversário", "Hair Nutri", etc. para reutilizar nas campanhas.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {filteredTemplates.map((t) => {
            const cat = CATEGORIES.find((c) => c.value === t.category) || { label: t.category };
            return (
              <Card key={t.template_id} className="border border-border bg-card p-4 shadow-soft rounded-md flex flex-col gap-3" data-testid={`template-card-${t.template_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{cat.label}</div>
                    <div className="font-medium text-sm truncate">{t.name}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(t)} data-testid={`edit-${t.template_id}`} title="Editar">
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(t)} data-testid={`delete-${t.template_id}`} title="Excluir">
                      <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground whitespace-pre-wrap line-clamp-4 bg-secondary/30 p-2 rounded">
                  {t.text}
                </div>
                {t.attachments && t.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {t.attachments.map((a) => {
                      const isImg = (a.content_type || "").startsWith("image/");
                      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${a.url}`;
                      return (
                        <div key={a.attachment_id} className="flex items-center gap-1 text-[10px] bg-secondary px-2 py-1 rounded">
                          {isImg ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                          <a href={fullUrl} target="_blank" rel="noreferrer" className="hover:underline" title={a.filename}>
                            {a.filename.length > 18 ? a.filename.slice(0, 18) + "…" : a.filename}
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(t, a.attachment_id)}
                            className="text-destructive hover:opacity-70 ml-0.5"
                            title="Remover"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                  <label className="cursor-pointer text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <Paperclip className="h-3 w-3" strokeWidth={1.5} />
                    Anexar
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadAttachment(t, f);
                        e.target.value = "";
                      }}
                      data-testid={`attach-input-${t.template_id}`}
                    />
                  </label>
                  <Button
                    size="sm"
                    onClick={() => { setSendTemplate(t); setSendOpen(true); }}
                    data-testid={`send-wa-${t.template_id}`}
                    style={{ background: "#16a34a" }}
                    className="text-white hover:opacity-90"
                    title="Enviar via WhatsApp"
                  >
                    <MessageCircle className="h-3 w-3 mr-1" strokeWidth={1.5} />
                    Enviar WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(t.text);
                      toast.success("Texto copiado");
                    }}
                    title="Copiar texto"
                    data-testid={`copy-${t.template_id}`}
                  >
                    <Send className="h-3 w-3 mr-1" strokeWidth={1.5} />
                    Copiar
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Variables hint */}
      <Card className="border border-dashed border-border bg-secondary/20 p-3 text-xs">
        <p className="font-medium mb-1">💡 Variáveis disponíveis no texto:</p>
        <p className="text-muted-foreground">
          <code className="bg-background px-1.5 rounded">{`{nome}`}</code>,{" "}
          <code className="bg-background px-1.5 rounded">{`{primeiro_nome}`}</code>,{" "}
          <code className="bg-background px-1.5 rounded">{`{telefone}`}</code>
          {" — substituídos automaticamente quando você disparar a campanha."}
        </p>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Template" : "Novo Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Corte +90 dias outono"
                data-testid="tpl-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Categoria</Label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="tpl-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Status após disparo</Label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                  value={form.default_funnel_status || ""}
                  onChange={(e) => setForm({ ...form, default_funnel_status: e.target.value })}
                  data-testid="tpl-funnel"
                >
                  {FUNNEL_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Texto da mensagem</Label>
              <Textarea
                rows={6}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Oi {primeiro_nome}! Faz tempo que não nos vemos 💕 Que tal agendar um horário?"
                data-testid="tpl-text"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Use {`{nome}`}, {`{primeiro_nome}`}, {`{telefone}`} para personalização.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="tpl-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk WhatsApp send dialog */}
      <BulkSendDialog
        open={sendOpen}
        template={sendTemplate}
        onClose={() => { setSendOpen(false); setSendTemplate(null); }}
      />
    </div>
  );
}

/* ============= Bulk WhatsApp Send Dialog ============= */

function BulkSendDialog({ open, template, onClose }) {
  const { apiCall } = useAuth();
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [search, setSearch] = useState("");
  const [funnelFilter, setFunnelFilter] = useState("");
  const [selected, setSelected] = useState({});  // { client_id: true }
  const [sending, setSending] = useState(false);
  const [jobResult, setJobResult] = useState(null);
  const [funnelOnSend, setFunnelOnSend] = useState("");

  useEffect(() => {
    if (!open) { setSelected({}); setJobResult(null); setSearch(""); return; }
    setFunnelOnSend(template?.default_funnel_status || "");
    setLoadingClients(true);
    apiCall("get", "/clients")
      .then((r) => setClients(r.data || []))
      .catch(() => toast.error("Erro ao carregar clientes"))
      .finally(() => setLoadingClients(false));
  }, [open, template, apiCall]);

  const filtered = useMemo(() => {
    let xs = clients;
    if (funnelFilter) xs = xs.filter((c) => c.funnel_status === funnelFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      xs = xs.filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.phone || "").includes(q)
      );
    }
    return xs.filter((c) => (c.phone || "").trim()).slice(0, 500);
  }, [clients, search, funnelFilter]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleAll = () => {
    if (selectedCount === filtered.length) setSelected({});
    else {
      const m = {};
      filtered.forEach((c) => { m[c.client_id] = true; });
      setSelected(m);
    }
  };

  const previewText = useMemo(() => {
    if (!template?.text) return "";
    const first = filtered.find((c) => selected[c.client_id]) || filtered[0];
    if (!first) return template.text;
    const nome = first.name || "";
    return (template.text || "")
      .replaceAll("{nome}", nome)
      .replaceAll("{{nome}}", nome)
      .replaceAll("{primeiro_nome}", nome.split(" ")[0] || "")
      .replaceAll("{{primeiro_nome}}", nome.split(" ")[0] || "")
      .replaceAll("{telefone}", first.phone || "")
      .replaceAll("{{telefone}}", first.phone || "");
  }, [template, selected, filtered]);

  const send = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) {
      toast.error("Selecione pelo menos 1 cliente");
      return;
    }
    if (!template) return;
    setSending(true);
    try {
      const r = await apiCall("post", "/whatsapp/campaigns/bulk-send", {
        template_id: template.template_id,
        client_ids: ids,
        funnel_status_on_send: funnelOnSend || null,
      });
      setJobResult(r.data);
      toast.success(`${r.data.queued} mensagens na fila`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao disparar");
    } finally {
      setSending(false);
    }
  };

  if (!template) return null;

  const FUNNEL_LABELS = {
    amarelo: "🟡 Educativa",
    laranja: "🟠 Convite agenda",
    verde: "🟢 Agendou",
    azul: "🔵 Atendida",
    vermelho: "🔴 Sem resposta",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="bulk-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-700" strokeWidth={1.5} />
            Enviar via WhatsApp · {template.name}
          </DialogTitle>
        </DialogHeader>

        {jobResult ? (
          <div className="space-y-3 text-sm">
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" strokeWidth={1.5} />
                <span className="font-medium text-emerald-900">Campanha disparada!</span>
              </div>
              <p className="text-xs text-emerald-900">
                <b>{jobResult.queued}</b> mensagens na fila · estimativa <b>~{jobResult.estimated_minutes} min</b> com delay anti-ban (8–30s entre cada envio).
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                Job ID: <code>{jobResult.job_id}</code>
              </p>
            </div>
            {(jobResult.skipped || []).length > 0 && (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs">
                <div className="font-medium text-amber-900 mb-1">
                  {jobResult.skipped.length} cliente(s) pulado(s):
                </div>
                {jobResult.skipped.slice(0, 8).map((s, i) => (
                  <div key={i} className="text-amber-800">· {s.client_name} — {s.reason}</div>
                ))}
                {jobResult.skipped.length > 8 && (
                  <div className="text-amber-800">… +{jobResult.skipped.length - 8} mais</div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={onClose} data-testid="bulk-close-btn">Fechar</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {/* Filters row */}
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                placeholder="Buscar nome ou telefone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-[220px] text-xs"
                data-testid="bulk-search"
              />
              <select
                value={funnelFilter}
                onChange={(e) => setFunnelFilter(e.target.value)}
                className="h-8 px-2 text-xs border border-input rounded-md bg-background"
                data-testid="bulk-funnel-filter"
              >
                <option value="">Funil: Todos</option>
                {Object.entries(FUNNEL_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={toggleAll} className="h-8 text-xs" data-testid="bulk-select-all">
                {selectedCount === filtered.length && filtered.length > 0 ? "Limpar" : `Selecionar todos (${filtered.length})`}
              </Button>
              <span className="text-xs text-muted-foreground ml-auto">
                <b className="text-foreground">{selectedCount}</b> selecionado(s)
              </span>
            </div>

            {/* Client list */}
            <div className="border border-border rounded-md max-h-[260px] overflow-y-auto" data-testid="bulk-client-list">
              {loadingClients ? (
                <div className="p-3 text-center text-xs text-muted-foreground">Carregando...</div>
              ) : filtered.length === 0 ? (
                <div className="p-3 text-center text-xs text-muted-foreground">Nenhum cliente encontrado.</div>
              ) : filtered.map((c) => (
                <label
                  key={c.client_id}
                  className="flex items-center gap-2 px-2 py-1.5 border-b border-border/40 hover:bg-secondary/30 cursor-pointer text-xs"
                  data-testid={`bulk-client-${c.client_id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[c.client_id]}
                    onChange={(e) => setSelected({ ...selected, [c.client_id]: e.target.checked })}
                  />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground">{c.phone}</span>
                  {c.funnel_status && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary">{FUNNEL_LABELS[c.funnel_status] || c.funnel_status}</span>
                  )}
                </label>
              ))}
            </div>

            {/* Preview */}
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Pré-visualização (1º selecionado)</Label>
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-xs whitespace-pre-wrap min-h-[60px]">
                {previewText || "Selecione um cliente pra ver a prévia"}
              </div>
            </div>

            {/* Funnel-on-send */}
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Atualizar funil após envio</Label>
              <select
                value={funnelOnSend}
                onChange={(e) => setFunnelOnSend(e.target.value)}
                className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background"
                data-testid="bulk-funnel-on-send"
              >
                <option value="">— Não alterar —</option>
                {Object.entries(FUNNEL_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={sending}>Cancelar</Button>
              <Button
                onClick={send}
                disabled={sending || selectedCount === 0}
                style={{ background: "#16a34a" }}
                className="text-white hover:opacity-90"
                data-testid="bulk-send-btn"
              >
                {sending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} />Disparando...</>
                ) : (
                  <><MessageCircle className="h-4 w-4 mr-2" strokeWidth={1.5} />Disparar para {selectedCount}</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
